/* eslint-disable import/no-unused-modules */
import React, { useEffect, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import './styles.scss';
import { translate } from 'helpers/translateHelper';
import { hasDataSeries } from './helpers';
import { IconDownload, IconPopOut } from 'svgIcons/MotionPortalIcons';
import { Switch, GeneralButton } from '..';
import { map } from 'lodash';
import { useDispatch } from 'react-redux';
import { setMeasurementDialogParams } from '../../PowertrainAssetDetails/DetailedInformation/OperationalParametersV2/SingleChartView/Trend/TrendKPIList/actions';
import { assetMenuItems, actionTypes } from '../../PowertrainAssetDetails/DetailedInformation/Header/constants';
import { BEARING_TYPES, FFT_UNITS } from './constants';
import { chartViews } from '../../PowertrainAssetDetails/DetailedInformation/OperationalParametersV2/VibrationFFTV2/constants';
import IconBearingBPFI from 'svgIcons/MotionPortalIcons/IconBearingBPFI';
import IconBearingBSF from 'svgIcons/MotionPortalIcons/IconBearingBSF';
import IconBearingBPFO from 'svgIcons/MotionPortalIcons/IconBearingBPFO';
import useUserCapability from 'sharedHooks/useUserCapability';

// eslint-disable-next-line react/display-name
export const withPopOutRow = (components) => (props) => {
    return <Row id='popout-chart-row-button-container' className={`popout-chart-row justify-content-end ${props.options?.exporting?.enabled && 'popout-chart-row--condition-insights'}`}>
        {useMemo(() => map(components, (Component, idx) => <Component key={idx} {...props} />), [props])}
    </Row>;
};

// eslint-disable-next-line react/display-name
export const withRightButtonGroup = (components) => (props) => {
    return <Col md='auto' className='right-button-group'>
        {useMemo(() => map(components, (Component, idx) => <Component key={idx} {...props} />), [props])}
    </Col>;
};

// eslint-disable-next-line react/display-name
export const withLeftButtonGroup = (components) => (props) => {
    return <Col className='left-button-group'>
        {useMemo(() => map(components, (Component, idx) => <Component key={idx} {...props} />), [props])}
    </Col>;
};

export const ResetZoomButton = (props) => {
    const {
        chartOptions,
        isZoomed,
        setIsZoomed,
        resetServerZoom,
        hasKpi,
    } = props;
    return hasKpi && isZoomed && (chartOptions.enableNoDataZoom || hasDataSeries(chartOptions)) ? <GeneralButton
        className='reset-zoom-button'
        type='normal'
        text={translate('ABB.Powertrain.Frontend.btnResetZoomOperationalParameters')}
        disabled={false}
        onClick={() => { resetServerZoom && resetServerZoom(); setIsZoomed(false); }}
    /> : null;
};

export const XAxisToggleButton = (props) => {
    const {
        chartOptions,
        hasKpi,
        chartInstance,
        selectedXAxisUnit,
        setSelectedXAxisUnit
    } = props;

    useEffect(() => {
        if (chartInstance?.xAxis?.length === 2) {
            if (selectedXAxisUnit === FFT_UNITS.HZ) {
                chartInstance.xAxis[0].update({ visible: true });
                chartInstance.xAxis[1].update({ visible: false });
            } else {
                chartInstance.xAxis[0].update({ visible: false });
                chartInstance.xAxis[1].update({ visible: true });
            }
        }
    }, [selectedXAxisUnit, chartInstance]);

    return hasKpi && hasDataSeries(chartOptions) ?
        <div className='x-axis-toggle-button'>
            <GeneralButton
                className={`${FFT_UNITS.HZ}-unit${selectedXAxisUnit === FFT_UNITS.HZ ? ' selected' : ''}`}
                type='normal'
                text={translate('ABB.Powertrain.Frontend.hzUnit')}
                disabled={false}
                onClick={() => setSelectedXAxisUnit(FFT_UNITS.HZ)}
            />
            <GeneralButton
                className={`${FFT_UNITS.RPM}-unit${selectedXAxisUnit === FFT_UNITS.RPM ? ' selected' : ''}`}
                type='normal'
                text={translate('ABB.Powertrain.Frontend.rpmUnit')}
                disabled={false}
                onClick={() => setSelectedXAxisUnit(FFT_UNITS.RPM)}
            />
        </div>
        : null;
};

export const BearingTypesToggleButton = (props) => {
    const {
        chartOptions,
        hasKpi,
        bearingFaultType,
        setBearingFaultType
    } = props;

    return hasKpi && hasDataSeries(chartOptions) ?
        <div className='bearing-type-toggle-button'>
            <GeneralButton
                className={`${BEARING_TYPES.DE}-type${bearingFaultType === BEARING_TYPES.DE ? ' selected' : ''}`}
                type='normal'
                text={translate('ABB.Powertrain.Frontend.bearingTypeDE')}
                disabled={false}
                onClick={() => setBearingFaultType(BEARING_TYPES.DE)}
            />
            <GeneralButton
                className={`${BEARING_TYPES.NDE}-type${bearingFaultType === BEARING_TYPES.NDE ? ' selected' : ''}`}
                type='normal'
                text={translate('ABB.Powertrain.Frontend.bearingTypeNDE')}
                disabled={false}
                onClick={() => setBearingFaultType(BEARING_TYPES.NDE)}
            />
        </div>
        : null;
};

export const BearingFaultFrequencies = (props) => {
    const {
        chartOptions,
        hasKpi,
        bearingFrequencies,
        setBearingFaultFrequencies
    } = props;

    return hasKpi && hasDataSeries(chartOptions) ?
        <div className='bearing-fault-frequency-button'>
            <GeneralButton
                className={bearingFrequencies.BSF ? 'selected' : ''}
                type='discreet'
                icon={<IconBearingBSF />}
                text={translate('ABB.Powertrain.Frontend.bearingFaultBSF')}
                disabled={false}
                onClick={() => setBearingFaultFrequencies({ ...bearingFrequencies, BSF: !bearingFrequencies.BSF })}
            />
            <GeneralButton
                className={bearingFrequencies.BPFI ? 'selected' : ''}
                type='discreet'
                icon={<IconBearingBPFI />}
                text={translate('ABB.Powertrain.Frontend.bearingFaultBPFI')}
                disabled={false}
                onClick={() => setBearingFaultFrequencies({ ...bearingFrequencies, BPFI: !bearingFrequencies.BPFI })}
            />
            <GeneralButton
                className={bearingFrequencies.BPFO ? 'selected' : ''}
                type='discreet'
                icon={<IconBearingBPFO />}
                text={translate('ABB.Powertrain.Frontend.bearingFaultBPFO')}
                disabled={false}
                onClick={() => setBearingFaultFrequencies({ ...bearingFrequencies, BPFO: !bearingFrequencies.BPFO })}
            />
        </div>
        : null;
};

export const ChartViewToggleButton = (props) => {
    const {
        hasKpi,
        selectedChartView,
        setSelectedChartView
    } = props;

    const userCapabilities = useUserCapability();

    const handleChartViewToggle = (view) => {
        setSelectedChartView(view);
    };

    return hasKpi ? <div className='chart-view-button'>
        <GeneralButton
            className={`single${selectedChartView === chartViews.FFT2D ? ' selected' : ''}`}
            type='normal'
            text={translate('ABB.Powertrain.Frontend.2DChartView')}
            disabled={false}
            onClick={() => handleChartViewToggle(chartViews.FFT2D)}
        />
        <GeneralButton
            className={`waterfall${selectedChartView === chartViews.FFT3D ? ' selected' : ''}`}
            type='normal'
            text={translate('ABB.Powertrain.Frontend.3DChartView')}
            disabled={false}
            onClick={() => handleChartViewToggle(chartViews.FFT3D)}
        />
        {userCapabilities.hasFFTEChart3DCapability() &&
            <GeneralButton
                className={`waterfall-360${selectedChartView === chartViews.ECHART3D ? ' selected' : ''}`}
                type='normal'
                text={translate('ABB.Powertrain.Frontend.echart3DView')}
                disabled={false}
                onClick={() => handleChartViewToggle(chartViews.ECHART3D)}
            />
        }
    </div> : null;
};

export const PopOutButton = (props) => {
    const {
        chartOptions,
        showChartPopout,
        setChartPopOut,
        setZooming,
        isHideDownload
    } = props;
    return !showChartPopout && hasDataSeries(chartOptions) && !isHideDownload ?
        <button
            className='popout-button'
            onClick={() => {
                setZooming(true);
                setChartPopOut(true);
            }}
            type='button'><IconPopOut />
        </button> : null;
};

export const EchartDownloadButton = (props) => {
    const {
        chartOptions,
        downloadChart,
        chartInstance
    } = props;
    return hasDataSeries(chartOptions) ?
        <button
            className='echart-download-button'
            onClick={() => downloadChart(chartInstance)}
            type='button'>
            <IconDownload width='16px' height='16px' />
        </button> : null;
};

export const DownloadAll = (props) => {
    const dispatch = useDispatch();

    const downloadMeasurementGroup = () => {
        const kpis = props.selectedTrendKPIs;

        dispatch(setMeasurementDialogParams({
            measurementTypeIds: kpis
        }));
        dispatch({ type: actionTypes.SELECT_MENU_ITEM, selectedMenuItem: assetMenuItems.EXPORT_MEASUREMENTS });
    };

    const {
        chartOptions,
        showChartPopout,
        isHideDownload,
        hideDownloadButton = false // HIDE BUTTON IF TRUE
    } = props;


    return !showChartPopout && hasDataSeries(chartOptions) && !hideDownloadButton && !isHideDownload ?
        <button className='download-all' onClick={downloadMeasurementGroup} type='button'>
            {translate('ABB.Powertrain.Frontend.downloadMeasurementsLabel')}
        </button> : null;
};

export const LastSyncDate = (props) => {
    const {
        lastMeasurementLabel,
        labelLastSync
    } = props;
    return lastMeasurementLabel && <div className='last-sync-date'>
        <span>{labelLastSync}</span>
        <span>{lastMeasurementLabel}</span>
    </div>;
};

export const EnableUniqueScaling = (props) => {
    const {
        uniqueScaling,
        enableUniqueScaling,
        setUniqueScaling,
        chartOptions
    } = props;
    return enableUniqueScaling && hasDataSeries(chartOptions) &&
        <div className='unique-y-scale'>
            <Switch
                onChange={() => setUniqueScaling(!uniqueScaling)}
                checked={uniqueScaling}
                disabled={false}
                className='unique-y-scale-switch'
            />
            <div>{translate('ABB.Powertrain.Frontend.uniqueScaleLabel')}</div>
        </div>;
};

export const DefaultHeader = withPopOutRow([withRightButtonGroup([ResetZoomButton, PopOutButton, DownloadAll])]);
